import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Brain, Heart, User, Users } from 'lucide-react';
import { fadeInUp, staggerContainer } from '@/lib/motion';
import { services } from '@/data/index';
import { ServiceCard } from '@/components/Cards';
import { Button } from '@/components/ui/button';

const individualServices = services.filter(service => 
  service.category === 'psychologia' || service.category === 'seksuologia'
);

export default function ServicesIndividual() {
  return (
    <div className="flex flex-col w-full">
      {/* Hero Section */}
      <section className="relative py-24 lg:py-32 bg-background">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="text-center max-w-4xl mx-auto"
          >
            <motion.h1
              variants={fadeInUp}
              className="text-5xl lg:text-7xl font-serif text-foreground mb-6"
            >
              Usługi <span className="text-accent">indywidualne</span>
            </motion.h1>
            <motion.p
              variants={fadeInUp}
              className="text-xl text-muted-foreground leading-relaxed mb-8"
            >
              Profesjonalne wsparcie psychologiczne i seksuologiczne dostosowane do Twoich indywidualnych potrzeb. 
              Każda sesja to bezpieczna przestrzeń do rozwoju i zmiany.
            </motion.p>
            <motion.div variants={fadeInUp}>
              <Button asChild size="lg" className="rounded-full px-8">
                <a href="https://www.znanylekarz.pl/rachela-nowak/psycholog/kalisz" target="_blank" rel="noopener noreferrer">
                  Umów konsultację <ArrowRight className="ml-2 w-4 h-4" />
                </a>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 lg:py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center max-w-2xl mx-auto mb-16"
          >
            <motion.h2 variants={fadeInUp} className="text-3xl lg:text-4xl font-serif text-foreground mb-6">
              Obszary specjalizacji
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-lg text-muted-foreground">
              Każdy proces dopasowuję do indywidualnych potrzeb klienta, wykorzystując interdyscyplinarne doświadczenie.
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto"
          >
            {individualServices.map((service, index) => (
              <motion.div key={service.id} variants={fadeInUp} custom={index}>
                <ServiceCard service={service} />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 lg:py-24 bg-background">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="max-w-4xl mx-auto"
          >
            <motion.h2 variants={fadeInUp} className="text-3xl lg:text-4xl font-serif text-center text-foreground mb-12">
              Dlaczego warto wybrać terapię indywidualną?
            </motion.h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <motion.div variants={fadeInUp} className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2">Indywidualne podejście</h3>
                  <p className="text-muted-foreground">
                    Każda sesja jest dostosowana do Twoich unikalnych potrzeb, tempa i celów terapeutycznych.
                  </p>
                </div>
              </motion.div>

              <motion.div variants={fadeInUp} className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Brain className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2">Metody oparte na dowodach</h3>
                  <p className="text-muted-foreground">
                    Wykorzystuję techniki CBT i inne metody potwierdzone badaniami naukowymi.
                  </p>
                </div>
              </motion.div>

              <motion.div variants={fadeInUp} className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Heart className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2">Bezpieczna przestrzeń</h3>
                  <p className="text-muted-foreground">
                    Tworzę atmosferę pełną zrozumienia, bez osądzania, gdzie możesz być sobą.
                  </p>
                </div>
              </motion.div>

              <motion.div variants={fadeInUp} className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Users className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2">Kompleksowe wsparcie</h3>
                  <p className="text-muted-foreground">
                    Łączę wiedzę z psychologii, seksuologii i mediacji dla pełnego wsparcia.
                  </p>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-24 bg-primary/5">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center max-w-3xl mx-auto"
          >
            <motion.h2 variants={fadeInUp} className="text-3xl lg:text-4xl font-serif text-foreground mb-6">
              Gotowy na pierwszą sesję?
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-lg text-muted-foreground leading-relaxed mb-8">
              Zapraszam na konsultację wstępną, podczas której omówimy Twoje potrzeby 
              i dobierzemy odpowiednią formę wsparcia.
            </motion.p>
            <motion.div variants={fadeInUp} className="flex flex-wrap justify-center gap-4">
              <Button asChild size="lg" className="rounded-full px-8">
                <a href="https://www.znanylekarz.pl/rachela-nowak/psycholog/kalisz" target="_blank" rel="noopener noreferrer">
                  Umów pierwszą wizytę <ArrowRight className="ml-2 w-4 h-4" />
                </a>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}